import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { NavLink, NavLinkProps, useNavigate } from "react-router"
import { User, UserRole, useUser } from "@/apis/bff"
import { cn } from "@/lib/utils"
import { Country } from "@/types/__generated__/union-types"

interface MenuItemProps extends NavLinkProps {
  children: React.ReactNode
}

const MenuItem = ({ to, children, ...props }: MenuItemProps) => {
  return (
    <NavLink
      to={`admin/${to}`}
      className={({ isActive }) =>
        cn(
          "hover:bg-surface-hovered focus-visible:outline-primary-focused active:bg-primary-surface-focused rounded-md px-4 py-3.5 text-sm font-medium transition-all focus-visible:outline-offset-2",
          isActive && "bg-primary-surface text-on-primary-surface hover:bg-primary-surface-hovered",
        )
      }
      {...props}>
      <span className="truncate">{children}</span>
    </NavLink>
  )
}

interface ChildSubNavigationItem {
  title: string
  to: string
  roles: UserRole[]
  countries?: Country[]
}

interface SubNavigationItem {
  title: string
  children: ChildSubNavigationItem[]
}

const subNavigation: SubNavigationItem[] = [
  {
    title: "admin.subNavigation.yourAccount",
    children: [
      {
        title: "admin.treatment.pageTitle.owner",
        to: "treatment-plan-templates",
        roles: ["Doctor", "NursingProfessional", "MedicalSupervisor"],
      },
    ],
  },
  {
    title: "admin.subNavigation.administrator",
    children: [
      {
        title: "admin.news.pageTitle",
        to: "news",
        roles: ["MedicalSupervisor"],
      },
      {
        title: "admin.capacity.pageTitle",
        to: "capacity",
        roles: ["MedicalSupervisor", "CustomerServiceCenter"],
      },
      {
        title: "admin.advanced.pageTitle",
        to: "advanced",
        roles: ["MedicalSupervisor"],
      },
      {
        title: "admin.agentSkills.pageTitle",
        to: "agent-skills",
        roles: ["MedicalSupervisor"],
      },
      {
        title: "admin.agentPlannings.pageTitle",
        to: "agent-plannings",
        roles: ["MedicalSupervisor"],
      },
      {
        title: "admin.sasisLookup.pageTitle",
        to: "sasis-lookup",
        roles: ["MedicalSupervisor"],
      },
      {
        title: "admin.betterDoc.pageTitle",
        to: "better-doc",
        roles: ["MedicalSupervisor"],
        countries: ["DE"],
      },
      {
        title: "Call Transcript Simulator",
        to: "call-transcript-simulator",
        roles: ["MedicalSupervisor"],
      },
    ],
  },
]

const hasNavigationItemAccess = (navigationItem: ChildSubNavigationItem, user: User) =>
  user.isOneOf(navigationItem.roles) &&
  (!navigationItem.countries || navigationItem.countries.some((country) => user.countries.includes(country)))

const SubNavigation = () => {
  const { t } = useTranslation()
  const user = useUser()

  return (
    <div className="border-border bg-surface z-0 grid h-full min-w-60 translate-x-0 transform content-start gap-4 border-r pt-4">
      <div className="grid-rows-auto-1fr grid [&>*:last-child]:border-none">
        {subNavigation.map((subNav, index) => {
          const { title, children } = subNav
          const hasSubNav = subNavigation[index].children.find((nav) => hasNavigationItemAccess(nav, user))
          return (
            hasSubNav && (
              <div key={title} className="border-b py-8">
                <span className="text-on-surface-subdued px-6 text-xs">{t(title)}</span>
                <div className="grid gap-0.5 p-2">
                  {children.map((nav) => {
                    const { to, title } = nav
                    return (
                      hasNavigationItemAccess(nav, user) && (
                        <MenuItem data-testid={`${to}-nav-link`} key={to} to={to}>
                          {t(title)}
                        </MenuItem>
                      )
                    )
                  })}
                </div>
              </div>
            )
          )
        })}
      </div>
    </div>
  )
}

const Admin = () => {
  const user = useUser()
  const navigate = useNavigate()
  const hasNavigated = useRef(false)

  useEffect(() => {
    if (!hasNavigated.current) {
      const firstValidChild = subNavigation.flatMap((sn) => sn.children).find((sn) => hasNavigationItemAccess(sn, user))
      if (firstValidChild) {
        navigate(`${firstValidChild.to}`, { replace: true })
        hasNavigated.current = true
      }
    }
  }, [navigate, user])

  return null
}

export { hasNavigationItemAccess, subNavigation, SubNavigation, Admin }
